/**
 * The record header — breadcrumb, mark, title, tags, one meta line, three
 * pills, and the actions.
 *
 * Every measurement is from `design/mockups/16-record-page/record-contact.png`
 * and the plan's §3.2: identity row `12px 24px 6px` with an 18px gap, a 56px
 * mark, a 22px/700 title, 19px tags, and a pills row indented to 98px so the
 * numbers line up under the title rather than under the mark.
 *
 * The meta line is the fussy part and worth the words: it is one line, and
 * when it does not fit it loses items from the right rather than wrapping. A
 * header that wraps changes height, and a header that changes height moves
 * the tabs under somebody's cursor.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { Button } from '../components/primitives.js'
import { ContextMenu, type MenuItem } from '../components/context-menu.js'
import { IconChevronLeft, IconMore } from '../frame/icons.js'
import type { RecordPill } from '../lib/use-record.js'

export interface RecordTag {
  label: string
  /** A semantic pair from §3.1, as `text/background` CSS colours. */
  tone?: { fg: string; bg: string }
  /** A glyph before the words — the visit's division tag (folder 50). Opt-in. */
  icon?: ReactNode
}

export interface RecordMeta {
  icon?: ReactNode
  text: string
  href?: string
  onClick?: () => void
}

export interface RecordAction {
  label: string
  icon?: ReactNode
  onClick: () => void
}

export interface RecordHeaderProps {
  phone: boolean
  /**
   * Something else is drawing the rule under this header — on a phone, the
   * quick actions.
   *
   * They are part of the same block of white: the identity, the numbers and
   * the four buttons are one header that happens to be three rows tall. A
   * rule between the pills and the buttons cut it in half and made the
   * buttons look like the first thing in the content rather than the last
   * thing in the header.
   */
  flush?: boolean
  breadcrumb: { label: string; href?: string }[]
  mark: ReactNode
  title: string
  tags: RecordTag[]
  meta: RecordMeta[]
  pills: RecordPill[]
  primary: RecordAction | null
  /**
   * The buttons left of the primary, in the order they are given.
   *
   * A list rather than one slot: every record wants Edit up here beside
   * whatever else it offers — Navigate on a property, Call on a contact — and
   * a single `secondary` meant one of them had to hide in the ⋯ menu. An
   * action that every record has is not a "more" action.
   */
  actions: RecordAction[]
  menu: MenuItem[]
  onBack: () => void
  /**
   * The visit page's header (`docs/visit-page.md` §7.2, §19; folder 50,
   * Boards 1, 13, 14, 15, 16) — **opt-in, additive** (lead decision 13): the
   * pills sit inside the title column under a meta line that wraps, the row is
   * top-aligned, the buttons are 36 (40 on an iPad) with a 10 px radius, and
   * on a phone the name runs to two lines over a sub-line. Left out, the
   * header is exactly what every other record draws.
   */
  layout?: 'stacked'
  /** Stacked only: what the board draws under the header — Board 13 B's line under it. */
  below?: ReactNode
  /** Stacked phone only: the line under the name (*J-3271 · Whitmer Residence · Day 2 of 3*). */
  subline?: string
  /** Stacked phone only: the top row's pencil (Board 16). */
  onEdit?: () => void
  /**
   * Folder 52's buttons (`docs/job-stages.md` §1, Board 1) — **opt-in,
   * additive**: on a desktop the secondary white and the primary green at
   * 36 px with a 10 px radius, 14 px sides and 12.5 semibold words, and the
   * vertical ⋯ beside them, which is tinted (accent-soft, accent-line,
   * accent-dark) while its menu is open, as the board draws it. Left out, the
   * buttons are today's. The phone's header is unchanged either way.
   */
  buttons?: 'stages'
}

/** Board 1's button (folder 52): 36 tall, a 10 px radius, 14 px sides, 12.5 semibold, the glyph 6 px from the words. */
const STAGE_BUTTON = 'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-button-toolbar border px-3.5 text-[12.5px] font-semibold'

export function RecordHeader(props: RecordHeaderProps) {
  if (props.layout === 'stacked') {
    return props.phone ? <StackedPhoneHeader {...props} /> : <StackedHeader {...props} />
  }
  return <DefaultHeader {...props} />
}

function DefaultHeader({
  phone, flush, breadcrumb, mark, title, tags, meta, pills, primary, actions, menu, onBack, buttons,
}: RecordHeaderProps) {
  const [open, setOpen] = useState<{ x: number; y: number } | null>(null)
  const moreAt = useRef<HTMLButtonElement>(null)

  const openMenu = () => {
    const box = moreAt.current?.getBoundingClientRect()
    /* Folder 52's menu on a whole pixel, so its glyphs draw as crisply as the board's. */
    const y = box ? (buttons === 'stages' ? Math.round(box.bottom + 4) : box.bottom + 4) : 0
    setOpen(box ? { x: box.right, y } : null)
  }

  if (phone) {
    return (
      <header
        className={cn('shrink-0 bg-surface px-4 pb-3 pt-2', !flush && 'border-b border-line')}
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Back"
            onClick={onBack}
            className="-ml-2 flex h-9 w-9 items-center justify-center rounded-field text-text-muted"
          >
            <IconChevronLeft size={20} />
          </button>
          <span className="text-body text-text-muted">{breadcrumb[0]?.label}</span>
          <span className="ml-auto flex items-center gap-1">
            {menu.length > 0 && (
              <button
                ref={moreAt}
                type="button"
                aria-label="More actions"
                onClick={openMenu}
                className="flex h-9 w-9 items-center justify-center rounded-field text-text-muted"
              >
                <IconMore size={20} />
              </button>
            )}
          </span>
        </div>

        <div className="mt-1 flex items-start gap-3">
          <span className="shrink-0">{mark}</span>
          <div className="min-w-0 flex-1">
            <h1 className="m-0 truncate text-[22px] font-bold leading-tight tracking-[-0.01em]">
              {title}
            </h1>
            {/*
              * One fact, not five. A 390px line fits about one of them, and
              * the four it cannot fit used to be half an icon hanging off the
              * right edge. The phone number and the email are the two buttons
              * directly underneath, so nothing is lost by leaving them out.
              */}
            <MetaLine meta={meta.slice(0, 1)} />
            {tags.length > 0 && <TagRow tags={tags} className="mt-1.5 flex-wrap" />}
          </div>
        </div>

        {pills.length > 0 && <PillRow pills={pills} scroll className="mt-2.5" />}

        {open && <ContextMenu items={menu} x={open.x} y={open.y} onClose={() => setOpen(null)} />}
      </header>
    )
  }

  return (
    <header className="shrink-0 bg-surface">
      <nav className="flex items-center gap-1.5 px-6 pt-3 text-cell text-text-muted">
        {breadcrumb.map((crumb, index) => (
          <span key={`${crumb.label}-${index}`} className="flex items-center gap-1.5">
            {index > 0 && <span className="text-line-strong">/</span>}
            {crumb.href
              ? <a href={crumb.href} className="hover:underline">{crumb.label}</a>
              : <span className="text-text">{crumb.label}</span>}
          </span>
        ))}
      </nav>

      <div className="flex items-center gap-[18px] px-6 pb-1.5 pt-3">
        <span className="shrink-0">{mark}</span>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <h1 className="m-0 truncate text-[22px] font-bold leading-none tracking-[-0.01em]">
              {title}
            </h1>
            <TagRow tags={tags} />
          </div>
          <MetaLine meta={meta} />
        </div>

        {buttons === 'stages' ? (
          <div className="flex shrink-0 items-center gap-2 leading-[normal]" data-record-buttons="stages">
            {actions.map((action) => (
              <button key={action.label} type="button" onClick={action.onClick}
                data-button="secondary"
                className={cn(STAGE_BUTTON, 'border-line bg-surface text-text hover:bg-hover')}>
                {action.icon}
                {action.label}
              </button>
            ))}
            {primary && (
              <button type="button" onClick={primary.onClick}
                data-button="primary"
                className={cn(STAGE_BUTTON, 'border-accent bg-accent text-white hover:bg-accent-dark')}>
                {primary.icon}
                {primary.label}
              </button>
            )}
            {menu.length > 0 && (
              <button
                ref={moreAt}
                type="button"
                aria-label="More actions"
                aria-expanded={open !== null}
                onClick={openMenu}
                className={cn('inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-button-toolbar border',
                  open ? 'border-accent-line bg-accent-soft text-accent-dark'
                    : 'border-line bg-surface text-text hover:bg-hover')}
              >
                <ChromeGlyph name="more" size={16} />
              </button>
            )}
          </div>
        ) : (
        <div className="flex shrink-0 items-center gap-2">
          {actions.map((action) => (
            <Button
              key={action.label}
              onClick={action.onClick}
              className="h-9 gap-1.5 rounded-button px-3"
            >
              {action.icon}
              {action.label}
            </Button>
          ))}
          {primary && (
            <Button
              variant="primary"
              onClick={primary.onClick}
              className="h-9 gap-1.5 rounded-button px-3.5"
            >
              {primary.icon}
              {primary.label}
            </Button>
          )}
          {menu.length > 0 && (
            <button
              ref={moreAt}
              type="button"
              aria-label="More actions"
              onClick={openMenu}
              className="flex h-9 w-9 items-center justify-center rounded-button border border-line bg-surface text-text-muted hover:text-text"
            >
              <IconMore size={16} />
            </button>
          )}
        </div>
        )}
      </div>

      {/* Indented to line up under the title, not under the mark (§3.2). */}
      {pills.length > 0 && <PillRow pills={pills} className="pb-3.5 pl-[98px] pr-6 pt-1.5" />}

      {open && <ContextMenu items={menu} x={open.x} y={open.y} onClose={() => setOpen(null)} />}
    </header>
  )
}

// ---------------------------------------------------------------------------
// The stacked header (opt-in — folder 50)
// ---------------------------------------------------------------------------

/** The board's own glyphs for the stacked header's chrome (vertical ⋯, back, pencil). */
function ChromeGlyph({ name, size }: { name: 'more' | 'back' | 'edit'; size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
      strokeWidth={name === 'more' ? 2.4 : 2} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
      {name === 'more' && <><circle cx="12" cy="5" r="1.2" /><circle cx="12" cy="12" r="1.2" /><circle cx="12" cy="19" r="1.2" /></>}
      {name === 'back' && <path d="M15 6l-6 6 6 6" />}
      {name === 'edit' && <><path d="M4 20l1-4L16.5 4.5a2 2 0 0 1 3 3L8 19z" /><path d="M14 7l3 3" /></>}
    </svg>
  )
}

/** The iPad's header (Board 15): 20 px sides and 40 px buttons, below the desktop's 1280. */
function useTabletHeader(): boolean {
  const query = '(max-width: 1279px)'
  const [tablet, setTablet] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const list = window.matchMedia(query)
    const update = () => setTablet(list.matches)
    update()
    list.addEventListener('change', update)
    return () => list.removeEventListener('change', update)
  }, [])
  return tablet
}

function StackedHeader({
  breadcrumb, mark, title, tags, meta, pills, primary, actions, menu, below,
}: RecordHeaderProps) {
  const [open, setOpen] = useState<{ x: number; y: number } | null>(null)
  const moreAt = useRef<HTMLButtonElement>(null)
  const tablet = useTabletHeader()
  const openMenu = () => {
    const box = moreAt.current?.getBoundingClientRect()
    setOpen(box ? { x: box.right, y: box.bottom + 4 } : null)
  }
  const button = cn('inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] border font-semibold',
    tablet ? 'h-10 px-3.5 text-[13px]' : 'h-9 px-3.5 text-[12.5px]')

  return (
    <header className="shrink-0 bg-surface leading-[normal] tabular-nums" data-record-header="stacked">
      <nav className={cn('flex items-center gap-1.5 pt-3 text-[12.5px] text-text-muted', tablet ? 'px-5' : 'px-6')}>
        {breadcrumb.map((crumb, index) => (
          <span key={`${crumb.label}-${index}`} className="flex items-center gap-1.5">
            {index > 0 && <span className="text-line-strong">/</span>}
            {crumb.href
              ? <a href={crumb.href} className="hover:underline">{crumb.label}</a>
              : <span className="text-text">{crumb.label}</span>}
          </span>
        ))}
      </nav>

      <div className={cn('flex items-start gap-[18px] pb-3.5 pt-3', tablet ? 'px-5' : 'px-6')}>
        <span className="flex shrink-0">{mark}</span>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <h1 className="m-0 truncate text-[22px] font-bold tracking-[-0.01em]">{title}</h1>
            <TagRow tags={tags} />
          </div>
          {meta.length > 0 && (
            <div className="flex flex-wrap items-center gap-[18px]">
              {meta.map((item, index) => {
                const className = cn('inline-flex items-center gap-1.5 whitespace-nowrap text-[13px]',
                  item.href || item.onClick ? 'text-accent hover:underline' : 'text-text-muted')
                const inner = <>{item.icon}{item.text}</>
                return item.href
                  ? <a key={index} href={item.href} className={className} target={/^https?:/.test(item.href) ? '_blank' : undefined}
                    rel="noreferrer">{inner}</a>
                  : item.onClick
                    ? <button key={index} type="button" onClick={item.onClick} className={className}>{inner}</button>
                    : <span key={index} className={className}>{inner}</span>
              })}
            </div>
          )}
          {pills.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-2">
              {pills.map((pill) => (
                <span
                  key={pill.label}
                  data-pill={pill.internal ? 'internal' : 'plain'}
                  className={cn('box-content inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full border border-line px-3 text-[12.5px]',
                    pill.internal ? 'bg-surface-internal' : 'bg-surface')}
                >
                  {pill.internal && (
                    <span className="inline-flex" style={{ color: 'rgb(var(--status-kind-fee-fg))' }}>
                      <svg viewBox="0 0 24 24" width={11} height={11} fill="none" stroke="currentColor" strokeWidth={2.4}
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
                      </svg>
                    </span>
                  )}
                  <b className={cn('text-[14px] font-bold', pill.tone === 'danger' ? 'text-danger' : 'text-text')}>{pill.value}</b>
                  <span className="text-text-muted">{pill.label}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="ml-2.5 flex shrink-0 gap-2">
          {actions.map((action) => (
            <button key={action.label} type="button" onClick={action.onClick}
              className={cn(button, 'border-line bg-surface text-text hover:bg-hover')}>
              {action.icon}
              {action.label}
            </button>
          ))}
          {primary && (
            <button type="button" onClick={primary.onClick}
              className={cn(button, 'border-accent bg-accent text-white hover:bg-accent-dark')}>
              {primary.icon}
              {primary.label}
            </button>
          )}
          {menu.length > 0 && (
            <button
              ref={moreAt}
              type="button"
              aria-label="More actions"
              onClick={openMenu}
              className={cn('inline-flex shrink-0 items-center justify-center rounded-[10px] border border-line bg-surface text-text hover:bg-hover',
                /* Board 15 draws the iPad's ⋯ 40 inside its border (42 in all); Board 1's 36 with it. */
                tablet ? 'box-content h-10 w-10' : 'h-9 w-9')}
            >
              <ChromeGlyph name="more" size={16} />
            </button>
          )}
        </div>
      </div>

      {below}

      {open && <ContextMenu items={menu} x={open.x} y={open.y} onClose={() => setOpen(null)} />}
    </header>
  )
}

/** Board 16's phone header: back + the module, the pencil and ⋯; the mark beside a two-line name and its sub-line; the tags. */
function StackedPhoneHeader({
  breadcrumb, mark, title, tags, menu, onBack, subline, onEdit, below,
}: RecordHeaderProps) {
  const [open, setOpen] = useState<{ x: number; y: number } | null>(null)
  const moreAt = useRef<HTMLButtonElement>(null)
  const openMenu = () => {
    const box = moreAt.current?.getBoundingClientRect()
    setOpen(box ? { x: box.right, y: box.bottom + 4 } : null)
  }
  return (
    <header className="shrink-0 bg-surface leading-[normal] tabular-nums" data-record-header="stacked-phone">
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <button type="button" aria-label="Back" onClick={onBack}
          className="inline-flex h-9 w-9 items-center justify-center text-text">
          <ChromeGlyph name="back" size={20} />
        </button>
        <span className="text-[13px] text-text-muted">{breadcrumb[0]?.label}</span>
        <span className="flex-1" />
        {onEdit && (
          <button type="button" aria-label="Edit" onClick={onEdit}
            className="inline-flex h-9 w-9 items-center justify-center text-text">
            <ChromeGlyph name="edit" size={18} />
          </button>
        )}
        {menu.length > 0 && (
          <button ref={moreAt} type="button" aria-label="More actions" onClick={openMenu}
            className="inline-flex h-9 w-9 items-center justify-center text-text">
            <ChromeGlyph name="more" size={18} />
          </button>
        )}
      </div>
      <div className="flex flex-col gap-2.5 px-4 pb-3 pt-1">
        <div className="flex items-start gap-3">
          <span className="flex shrink-0">{mark}</span>
          <div className="flex min-w-0 flex-col">
            <h1 className="m-0 text-[19px] font-bold leading-[1.2] tracking-[-0.01em]">{title}</h1>
            {subline && <span className="mt-[3px] text-[12.5px] text-text-muted">{subline}</span>}
          </div>
        </div>
        {tags.length > 0 && <TagRow tags={tags} className="items-center" />}
      </div>
      {below}
      {open && <ContextMenu items={menu} x={open.x} y={open.y} onClose={() => setOpen(null)} />}
    </header>
  )
}

function TagRow({ tags, className }: { tags: RecordTag[]; className?: string }) {
  if (tags.length === 0) return null
  return (
    <div className={cn('flex shrink-0 gap-1.5', className)}>
      {/* Three, and the rest are on the record itself (§3.2). */}
      {tags.slice(0, 3).map((tag) => (
        <span
          key={tag.label}
          className={cn(
            'inline-flex h-[19px] items-center whitespace-nowrap rounded-tag px-1.5',
            'text-[11px] font-semibold',
            tag.icon ? 'gap-1' : null,
            // Without a pair it is the quiet tag from §3.6, not bare text.
            !tag.tone && 'bg-line-soft text-text-muted',
          )}
          style={tag.tone
            ? { color: tag.tone.fg, background: tag.tone.bg }
            : undefined}
        >
          {tag.icon}
          {tag.label}
        </span>
      ))}
    </div>
  )
}

/**
 * One line of facts, never two.
 *
 * `overflow: hidden` on a nowrap row is what drops items off the right rather
 * than wrapping them onto a second line — and each item is `shrink-0` so the
 * one that does not fit disappears whole instead of being squeezed into an
 * ellipsis of its own.
 */
function MetaLine({ meta }: { meta: RecordMeta[] }) {
  if (meta.length === 0) return null
  return (
    <div className="flex min-w-0 items-center gap-[18px] overflow-hidden whitespace-nowrap">
      {meta.map((item, index) => {
        const inner = (
          <>
            {item.icon}
            <span className="truncate">{item.text}</span>
          </>
        )
        const className = cn(
          'inline-flex shrink-0 items-center gap-1.5 text-body',
          item.href || item.onClick ? 'text-accent hover:underline' : 'text-text-muted',
        )
        return item.href
          ? <a key={index} href={item.href} className={className}>{inner}</a>
          : item.onClick
            ? (
              <button key={index} type="button" onClick={item.onClick} className={className}>
                {inner}
              </button>
            )
            : <span key={index} className={className}>{inner}</span>
      })}
    </div>
  )
}

/**
 * The three numbers. The last one goes red when money is late, and that is the
 * only colour in the row — which is what makes it readable from across a room.
 */
function PillRow({
  pills, className, scroll,
}: {
  pills: RecordPill[]
  className?: string
  /** One row that scrolls, for a phone — three pills do not fit across 390px. */
  scroll?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2',
        scroll
          ? 'no-scrollbar overflow-x-auto whitespace-nowrap'
          : 'flex-wrap',
        className,
      )}
    >
      {pills.map((pill) => (
        <span
          key={pill.label}
          className="inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-chip border border-line bg-bg px-3"
        >
          <b className={cn('tnum text-[14px] font-bold',
            pill.tone === 'danger' ? 'text-danger' : 'text-text')}>
            {pill.value}
          </b>
          <span className="text-cell text-text-muted">{pill.label}</span>
        </span>
      ))}
    </div>
  )
}
